import React, { useEffect, useState } from "react"
import ComponentsUtils from "../../utils/components"
import { PageConfig, PageConfigModel } from "../../models/pageConfig"
import ComponentService from "../../services/component"
import UIService from "../../services/UIService"
import DialogService from "../../services/dialog"
import { DialogType } from "../../models/dialog"
import ModuleService from "../../services/module"
import { BarsConfig } from "../../models/bars"
import GroupService from "../../services/group"
import UserService from "../../services/user"
import CwHeader from "./cw-header"
import { Color } from "../../models/colors"
import CwBlockUI from "./cw-blockUI"

// déclaration de la bibliothéque de particule de fond

const CwPage = (props: {
    page: PageConfigModel | undefined
}): React.JSX.Element => {
    // récupère la config de la page
    const { page } = props
    const config = page?.config
    const className = config?.className ? config.className : ""
    const background = config?.background
        ? UIService.getLinearGradByName(config.background)
        : ""
    // crée la liste des composants présent dans la page et son setter
    const [components, setComponents] = useState<Array<React.JSX.Element>>([])
    const [barConfig, setBarConfig] = React.useState<null | BarsConfig>(null)
    const [isBlocking, setIsBlocking] = React.useState<boolean>(false)

    /**
     * Interoge les modules et les groups pour avoir la config des barres pour l'user connecté
     * @returns Promise<BarsConfig | undefined>
     */
    const getBarsConfig = () => {
        const promise = new Promise<BarsConfig>((resolve, reject) => {
            // on récupère les modules à afficher
            ModuleService.getInstance()
                .getModule()
                .then(async (res) => {
                    if (res) {
                        // on récupère les config de la bar en fonction du groupe
                        // de l'utilisateur qui est connecté
                        const barConfigSetting =
                            await GroupService.getInstance().getBarConfig()
                        if (barConfigSetting && barConfigSetting.drawerConfig) {
                            // on attribut les modules à la config du drawer
                            // ( pour affichage des liens )
                            barConfigSetting.drawerConfig.modules = res
                            return barConfigSetting
                        }

                        throw Error("Pas de config de barre disponible")
                    } else {
                        throw Error("Pas de modules disponible")
                    }
                })
                .then((conf: BarsConfig | undefined) => {
                    if (conf) {
                        resolve({
                            appBarConfig: conf.appBarConfig,
                            drawerConfig: conf.drawerConfig,
                        })
                    } else {
                        reject(Error("Pas de config de barre disponible"))
                    }
                })
                .catch(async (err) => {
                    console.error(err)
                    await UserService.getInstance().logout()
                    window.location.reload()
                })
        })

        return promise
    }

    useEffect(() => {
        let subId: string | null = null
        /* initialisation des composants */
        if (page) {
            // création du context pour les composants et des abonnements pour cette page
            if (page.context) {
                // si la page à un context on s'abonne en cas de changement de page
                subId = DialogService.getInstance().subscribe(
                    "navigationService",
                    DialogType.REFRESH,
                    page.name,
                    () => {
                        setIsBlocking(true)
                        // apres la mise a jour du content on propage
                        // un REFRESH aux enfants de la page
                        if (page.context)
                            ComponentService.getInstance()
                                .createContext(page.context)
                                .then(() => {
                                    DialogService.getInstance().send({
                                        type: DialogType.REFRESH,
                                        sender: "pageRefesh",
                                    })
                                    setIsBlocking(false)
                                })
                                .catch((e: any) => {
                                    setIsBlocking(false)
                                    UIService.getInstance().showErrorToast(
                                        "Chargement des données de la page impossible.",
                                        "CwPage"
                                    )
                                })
                    }
                )
                ComponentService.getInstance()
                    .createContext(page.context)
                    .then(() => {
                        ComponentsUtils.initChildFieldsComponent(
                            page.components,
                            setComponents
                        )
                    })
                    .catch((err) => {
                        console.error(err)
                        UIService.getInstance().showErrorToast(
                            "Impossible de charger la page",
                            "CwPage"
                        )
                    })
            } else {
                ComponentsUtils.initChildFieldsComponent(
                    page.components,
                    setComponents
                )
            }
            // set la config des barres
            if (page?.config?.showBar)
                getBarsConfig().then((c) => {
                    // set la config des barres
                    setBarConfig(c)
                })
        }
        return () => {
            if (page && subId) DialogService.getInstance().unsubscribe(subId)
        }
    }, [page])

    /**
     * Fait le rendu des composants de la page
     * avec ou sans les barres d'application
     */
    const renderComponents = () => {
        return (
            <div className="root" style={{ background }}>
                {page?.config?.showBar && barConfig?.drawerConfig
                    ? ComponentService.getInstance().create({
                          key: `mainDrawer`,
                          name: `menuDrawer`,
                          type: "drawer",
                          config: barConfig.drawerConfig,
                      })
                    : null}

                <main
                    style={{ background }}
                    className={`${"noBarMainContent"} ${className}`}
                >
                    {page?.config?.showBar &&
                    barConfig?.appBarConfig &&
                    page.title ? (
                        <CwHeader
                            name="pageTitle"
                            type="header"
                            config={{
                                ...(barConfig?.appBarConfig as any),
                                title: page?.title,
                                titleColor: Color.darkViolet,
                                titleVariant: "h2",
                                textClassName: "clearHeader",
                                className: "clearHeaderContainer",
                            }}
                        />
                    ) : null}
                    {components}
                    <CwBlockUI
                        className="fullHeight fullWidth loaderBackDropPosition"
                        blocking={isBlocking}
                    ></CwBlockUI>
                </main>
            </div>
        )
    }

    return page ? renderComponents() : ComponentsUtils.renderLoader()
}

export default CwPage
